import { inject, Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  DocumentData,
  QueryDocumentSnapshot,
  collectionData,
  runTransaction,
  Query,
  endBefore,
  limitToLast,
  getCountFromServer
} from '@angular/fire/firestore';
import { AuthService } from './auth-service';
import { BehaviorSubject, forkJoin, from, map, Observable, of, switchMap, take } from 'rxjs';
import { Order, OrderStatus } from '../models/order';
import { Product } from '../models/product';
import { Filter } from '../models/filter';
import { SelectedProduct } from '../models/selected-product';

export interface OrderPage {
  orders: Order[];
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
}

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  private firestore = inject(Firestore);
  private authService = inject(AuthService);
  private currentPage = 1;
  private pageSize = 8;
  private lastDoc: any = null;
  private firstDoc: any = null;
  private orders$: Observable<Order[]> = of([]);
  private pageCount = 0;
  private totalCount = 0;
  private paginationInfo$ = new BehaviorSubject<PaginationInfo>({
    currentPage: 1,
    pageCount: 0,
    currentPageCount: 0,
    totalCount: 0,
    from: 0,
    to: 0
  });

  constructor() {
    this.currentPage = 1;
    this.firstDoc = null;
    this.lastDoc = null
  }


  private loadFirstPage(filter: OrderStatus): void {
    this.orders$ = this.getOrders('', filter);
  }

  getProductObservable(filter: OrderStatus) {
    this.currentPage = 1;
    this.lastDoc = null;
    this.firstDoc = null;
    this.getTotalOrdersCount(filter)
    this.loadFirstPage(filter);
    return this.orders$ as Observable<Order[]>
  }

  private async getTotalOrdersCount(
    filter: OrderStatus,
  ) {
    try {
      const constraints: any[] = [];

      const user = this.authService.currentUser;
      constraints.push(where('userId', '==', user!.id))

      // ---- FILTERS ----
      if (filter !== OrderStatus.ALL) constraints.push(where('status', '==', filter));
      const q = query(collection(this.firestore, 'orders'), ...constraints);

      const snapshot = await getCountFromServer(q);
      const count = snapshot.data().count || 0;
      this.totalCount = count;
      this.pageCount = Math.ceil(count / this.pageSize);
      this.updatePaginationInfo();

    } catch (error) {
      console.error('Error fetching total product count:', error);
    }
  }

  getPaginationInfoObservable() {
    return this.paginationInfo$ as Observable<PaginationInfo>
  }

  getOrders(
    type: 'next' | 'previous' | 'page' | '',
    filter: OrderStatus
  ): Observable<Order[]> {

    let q: Query;

    // -------------------------------
    // SORTING
    // -------------------------------
    let orderField: keyof Product = 'createdAt';
    let orderDirection: any = 'desc';

    // -------------------------------
    // BUILD QUERY
    // -------------------------------
    const buildQuery = (startSnap?: any, endSnap?: any, isPrev = false) => {
      const conditions: any[] = [];
      const user = this.authService.currentUser;
      conditions.push(where('userId', '==', user!.id))
      // ---- FILTERS ----
      if (filter !== OrderStatus.ALL) conditions.push(where('status', '==', filter));

      // ---- ORDER ----
      conditions.push(orderBy(orderField as string, orderDirection));

      // ---- PAGINATION (correct way) ----
      if (startSnap) conditions.push(startAfter(startSnap));
      if (endSnap) conditions.push(endBefore(endSnap));

      conditions.push(isPrev ? limitToLast(this.pageSize) : limit(this.pageSize));

      return query(collection(this.firestore, 'orders'), ...conditions);
    };

    // -------------------------------
    // PAGINATION LOGIC
    // -------------------------------
    if (type === 'next' && this.lastDoc) {
      q = buildQuery(this.lastDoc, null, false);
    } else if (type === 'previous' && this.firstDoc) {
      q = buildQuery(null, this.firstDoc, true);
    } else {
      q = buildQuery();
    }

    // -------------------------------
    // RUN QUERY AND UPDATE CURSORS
    // -------------------------------
    return from(getDocs(q)).pipe(
      map(snapshot => {
        const items = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        })) as Order[];

        // Save document snapshots for next/prev
        if (snapshot.docs.length > 0) {
          this.firstDoc = snapshot.docs[0];                 // <-- snapshot, not field
          this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
        }

        this.updatePaginationInfo(items.length);
        return items;
      })
    );
  }

  nextPage(filter: OrderStatus): Observable<Order[]> {
    if (this.currentPage < this.pageCount || this.pageCount === 0) {
      this.currentPage++;
      this.orders$ = this.getOrders('next', filter);
    }
    return this.orders$;
  }

  previousPage(filter: OrderStatus): Observable<Order[]> {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.orders$ = this.getOrders('previous', filter);
    }
    return this.orders$;
  }

  private updatePaginationInfo(currentPageCount?: number): void {
    const from = ((this.currentPage - 1) * this.pageSize) + 1;
    const to = Math.min(this.currentPage * this.pageSize, this.totalCount)

    this.paginationInfo$.next({
      currentPage: this.currentPage,
      pageCount: this.pageCount,
      currentPageCount: currentPageCount ?? this.paginationInfo$.value.currentPageCount,
      totalCount: this.totalCount,
      from,
      to
    })
  }

  /**
   * Get single order by ID
   */
  getOrder(id: string): Observable<Order | undefined> {
    const ref = doc(this.firestore, `orders/${id}`);
    return from(getDoc(ref)).pipe(
      map(snap => (snap.exists() ? (snap.data() as Order) : undefined))
    );
  }

  /**
   * Update order status
   */
  updateOrderStatus(id: string, status: OrderStatus) {
    const ref = doc(this.firestore, `orders/${id}`);
    return from(
      updateDoc(ref, {
        status,
        updatedAt: new Date().toISOString()
      })
    );
  }


  // createOrderTransaction(order: Order): Observable<void> {
  //   return from(
  //     runTransaction(this.firestore, async (transaction) => {

  //       const ordersRef = collection(this.firestore, 'orders');
  //       const orderDocRef = doc(ordersRef);

  //       for (const item of order.products) {

  //         if (!item.product.id) {
  //           throw new Error('Product ID is missing');
  //         }

  //         const productRef = doc(this.firestore, 'products', item.product.id!);
  //         const productSnap = await transaction.get(productRef);

  //         if (!productSnap.exists()) {
  //           throw new Error(`Product not found: ${item.id}`);
  //         }

  //         const product = productSnap.data() as Product;

  //         /* ---------- PRODUCT QTY ---------- */
  //         const newProductQty = product.quantity - item.quantity;
  //         if (newProductQty < 0) {
  //           throw new Error(`Insufficient stock for ${product.name}`);
  //         }

  //         /* ---------- COMBINATION QTY ---------- */
  //         let combinationFound = false;
  //         const updatedCombinations = (product.availableCombinations || []).map(c => {

  //           const sameSize = c.size === item.size;
  //           const sameColor = c.color?.code === item.color?.code;
  //           if (sameSize && sameColor) {
  //             const newComboQty = c.quantity - item.quantity;

  //             if (newComboQty < 0) {
  //               throw new Error(
  //                 `Insufficient stock for ${product.name} (${c.size}, ${c.color?.name})`
  //               );
  //             }

  //             combinationFound = true;

  //             return {
  //               ...c,
  //               quantity: newComboQty
  //             };
  //           }

  //           return c;
  //         });

  //         if (!combinationFound) {
  //           throw new Error(
  //             `Selected size/color combination not found for ${product.name}`
  //           );
  //         }

  //         /* ---------- UPDATE PRODUCT ---------- */
  //         transaction.update(productRef, {
  //           quantity: newProductQty,
  //           availableCombinations: updatedCombinations,
  //           updatedAt: new Date().toISOString()
  //         });

  //       }

  //       /* ---------- SAVE ORDER ---------- */
  //       transaction.set(orderDocRef, {
  //         ...order,
  //         id: orderDocRef.id,
  //         createdAt: new Date().toISOString(),
  //         updatedAt: new Date().toISOString()
  //       });
  //     })
  //   );
  // }

  createOrderTransaction(order: Order): Observable<void> {
    return from(
      runTransaction(this.firestore, async (transaction) => {

        const ordersRef = collection(this.firestore, 'orders');
        const orderDocRef = doc(ordersRef);

        /* ---------- STEP 1: PREPARE REFS ---------- */
        const productRefs = order.products.map(item => {
          if (!item.product.id) {
            throw new Error('Product ID is missing');
          }
          return doc(this.firestore, 'products', item.product.id);
        });

        /* ---------- STEP 2: READ ALL FIRST ---------- */
        const productSnaps = await Promise.all(
          productRefs.map(ref => transaction.get(ref))
        );

        /* ---------- STEP 3: PROCESS & VALIDATE ---------- */
        const updates: any[] = [];

        for (let i = 0; i < order.products.length; i++) {
          const item = order.products[i];
          if (item.product.id && (item.product.id.startsWith('custom') || item.product.id.startsWith('gift-box'))) {
            continue;
          }
          const snap = productSnaps[i];

          if (!snap.exists()) {
            throw new Error(`Product not found: ${item.product.id}`);
          }

          const product = snap.data() as Product;

          const newProductQty = product.quantity - item.quantity;
          if (newProductQty < 0) {
            throw new Error(`Insufficient stock for ${product.name}`);
          }

          let combinationFound = false;

          const updatedCombinations = (product.availableCombinations || []).map(c => {
            const sameSize = c.size === item.size;
            const sameColor = c.color?.code === item.color?.code;

            if (sameSize && sameColor) {
              const newComboQty = c.quantity - item.quantity;

              if (newComboQty < 0) {
                throw new Error(
                  `Insufficient stock for ${product.name} (${c.size}, ${c.color?.name})`
                );
              }

              combinationFound = true;

              return {
                ...c,
                quantity: newComboQty
              };
            }

            return c;
          });

          if (!combinationFound) {
            throw new Error(
              `Selected combination not found for ${product.name}`
            );
          }

          updates.push({
            ref: productRefs[i],
            data: {
              quantity: newProductQty,
              availableCombinations: updatedCombinations,
              updatedAt: new Date().toISOString()
            }
          });
        }

        /* ---------- STEP 4: WRITE ALL ---------- */
        for (const u of updates) {
          transaction.update(u.ref, u.data);
        }

        /* ---------- SAVE ORDER ---------- */
        transaction.set(orderDocRef, {
          ...order,
          id: orderDocRef.id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });

      })
    );
  }


  cancelOrderTransaction(orderId: string): Observable<void> {
    return from(
      runTransaction(this.firestore, async (transaction) => {

        const orderRef = doc(this.firestore, 'orders', orderId);
        const orderSnap = await transaction.get(orderRef);

        if (!orderSnap.exists()) {
          throw new Error('Order not found');
        }

        const order = orderSnap.data() as Order;

        // Prevent double cancellation
        if (order.status === OrderStatus.CANCELLED) {
          throw new Error('Order already cancelled');
        }

        if (order.status !== OrderStatus.PENDING) {
          throw new Error('Order cannot be cancelled at this stage');
        }

        /* ---------- RESTORE STOCK ---------- */
        for (const item of order.products) {
          if (item.product.id && (item.product.id.startsWith('custom') || item.product.id.startsWith('gift-box'))) {
            continue;
          }

          if (!item.product.id) {
            throw new Error('Product ID is missing');
          }

          const productRef = doc(this.firestore, 'products', item.product.id);
          const productSnap = await transaction.get(productRef);

          if (!productSnap.exists()) {
            throw new Error(`Product not found: ${item.product.id}`);
          }

          const product = productSnap.data() as Product;

          // Only restore stock if product is NOT always available
          // if (!product.isAvailable) {

          /* ---------- RESTORE PRODUCT QTY ---------- */
          const restoredProductQty = product.quantity + item.quantity;

          /* ---------- RESTORE COMBINATION QTY ---------- */
          let combinationFound = false;

          const updatedCombinations = (product.availableCombinations || []).map(c => {

            const sameSize = c.size === item.size;
            const sameColor = c.color?.code === item.color?.code;

            if (sameSize && sameColor) {
              combinationFound = true;

              return {
                ...c,
                quantity: c.quantity + item.quantity
              };
            }

            return c;
          });

          if (!combinationFound) {
            throw new Error(
              `Combination not found for ${product.name} (${item.size}, ${item.color?.name})`
            );
          }

          /* ---------- UPDATE PRODUCT ---------- */
          transaction.update(productRef, {
            quantity: restoredProductQty,
            availableCombinations: updatedCombinations,
            updatedAt: new Date().toISOString()
          });
          // }
        }

        /* ---------- UPDATE ORDER STATUS ---------- */
        transaction.update(orderRef, {
          status: OrderStatus.CANCELLED,
          updatedAt: new Date().toISOString()
        });

      })
    );
  }


  checkProductAvailability(
    item: SelectedProduct
  ): Observable<{ available: boolean; message?: string }> {

    if (item.product.id && (item.product.id.startsWith('custom') || item.product.id.startsWith('gift-box'))) {
      return of({ available: true });
    }

    const productRef = doc(this.firestore, 'products', item.product.id!);

    return from(getDoc(productRef)).pipe(
      map(snapshot => {

        if (!snapshot.exists()) {
          return { available: false, message: 'Product not found' };
        }

        const product = snapshot.data() as Product;

        /* ---------- TOTAL QTY CHECK ---------- */
        if (product.quantity < item.quantity) {
          return {
            available: false,
            message: `Only ${product.quantity} items available`
          };
        }

        /* ---------- COMBINATION CHECK ---------- */
        const combination = (product.availableCombinations || []).find(c =>
          c.size === item.size && c.color?.code === item.color?.code
        );

        if (!combination) {
          return {
            available: false,
            message: 'Selected size/color not available'
          };
        }

        /* ---------- COMBINATION QTY CHECK ---------- */
        if (combination.quantity < item.quantity) {
          return {
            available: false,
            message: `Only ${combination.quantity} items available for selected variant`
          };
        }

        return { available: true };
      })
    );
  }

  checkCartAvailability(
    cartItems: SelectedProduct[] | null
  ): Observable<{ available: boolean; message?: string }> {

    if (!cartItems || cartItems.length === 0) {
      return of({ available: false, message: 'Cart is empty' });
    }

    const requests = cartItems.map(item => {
      if (item.product.id && (item.product.id.startsWith('custom') || item.product.id.startsWith('gift-box'))) {
        return of({ available: true });
      }

      const productRef = doc(this.firestore, 'products', item.product.id!);

      return from(getDoc(productRef)).pipe(
        map(snapshot => {

          if (!snapshot.exists()) {
            return { available: false, message: `${item.product.name} not found` };
          }

          const product = snapshot.data() as Product;

          /* ---------- TOTAL QTY CHECK ---------- */
          if (product.quantity < item.quantity) {
            return {
              available: false,
              message: `${product.name}: Only ${product.quantity} items available`
            };
          }

          /* ---------- COMBINATION CHECK ---------- */
          const combination = (product.availableCombinations || []).find(c =>
            c.size === item.size && c.color?.code === item.color?.code
          );

          if (!combination) {
            return {
              available: false,
              message: `${product.name}: Selected size/color not available`
            };
          }

          if (combination.quantity < item.quantity) {
            return {
              available: false,
              message: `${product.name}: Only ${combination.quantity} items available for selected variant`
            };
          }

          return { available: true };
        })
      );
    });

    return forkJoin(requests).pipe(
      map(results => {
        const failed = results.find(r => !r.available);

        if (failed) {
          return failed; // return first error
        }

        return { available: true };
      })
    );
  }

}
